import { ISelectItem } from "@components/inputs/select/Select.types";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { SymbolConstantDef, SymbolSpecificDef, SymbolVariableDef } from "@components/smart/GeneralFieldDefinition";
import { createPath, IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrHealthInsuranceCompanyBankAccountEntity,
    IPrHealthInsuranceCompanyDetailEntity,
    PrHealthInsuranceCompanyDetailEntity,
    PrHealthInsuranceCompanyEntity,
    PrPayrollSettingEntity
} from "@odata/GeneratedEntityTypes";

import { BasicInputSizes, FieldType, GroupedField } from "../../../enums";
import { IFormDef } from "../../../views/formView/Form";
import { IDefinition, IGetDefinition } from "../../PageUtils";
import HealthInsuranceCompanyFormView from "./HealthInsuranceCompanyFormView";

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const form: IFormDef = {
        id: `${EntityTypeName.PrHealthInsuranceCompany}Form`,
        translationFiles: getDefinitions.translationFiles,
        formControl: HealthInsuranceCompanyFormView,
        fieldDefinition: {
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.HealthInsuranceCompanyCode)]: {
                isReadOnly: true
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.ShortName)]: {
                isReadOnly: true
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.DataBoxId)]: {
                isReadOnly: true
            },
            // [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.AccountNumber)]: {},
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.Region)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isRequired: true,
                isVisible: (args: IGetValueArgs) => {
                    const entity = args.storage.getValue(args.bindingContext.getParent()) as IPrHealthInsuranceCompanyDetailEntity;
                    return entity.HealthInsuranceCompany.BankAccounts?.length > 1;
                },
                fieldSettings: {
                    itemsFactory: (args: IGetValueArgs): Promise<ISelectItem[]> => {
                        const entity = args.storage.getValue(args.bindingContext.getParent()) as IPrHealthInsuranceCompanyDetailEntity;
                        const items = entity.HealthInsuranceCompany.BankAccounts?.map((ba: IPrHealthInsuranceCompanyBankAccountEntity) => {
                            return {
                                id: ba.RegionCode,
                                label: ba.Region.Name
                            };
                        });
                        return Promise.resolve(items);
                    }
                }
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.SymbolVariable)]: {
                ...SymbolVariableDef,
                groupedField: GroupedField.MultiStart
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.SymbolSpecific)]: {
                ...SymbolSpecificDef
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.SymbolConstant)]: {
                ...SymbolConstantDef
            },
            [createPath(PrPayrollSettingEntity.HealthInsuranceCompanies, PrHealthInsuranceCompanyDetailEntity.RemittanceInformation)]: {
                groupedField: GroupedField.MultiEnd,
                width: BasicInputSizes.L
            }
        },
        groups: [
            {
                id: "InsuranceCompanies",
                collection: PrPayrollSettingEntity.HealthInsuranceCompanies,
                collectionOrder: createPath(PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.HealthInsuranceCompanyCode),
                showGroupDivider: true,
                isRemovable: false,
                collectionItemTitle: (index: number, item: IEntity) => {
                    const insuranceCompany = item as IPrHealthInsuranceCompanyDetailEntity;
                    return insuranceCompany.HealthInsuranceCompany.Name;
                },
                rows: [
                    [
                        { id: createPath(PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.HealthInsuranceCompanyCode) },
                        { id: createPath(PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.ShortName) },
                        { id: createPath(PrHealthInsuranceCompanyDetailEntity.HealthInsuranceCompany, PrHealthInsuranceCompanyEntity.DataBoxId) },
                        // { id: PrHealthInsuranceCompanyDetailEntity.AccountNumber },
                        { id: PrHealthInsuranceCompanyDetailEntity.Region }
                    ], [
                        { id: PrHealthInsuranceCompanyDetailEntity.SymbolVariable },
                        { id: PrHealthInsuranceCompanyDetailEntity.SymbolSpecific },
                        { id: PrHealthInsuranceCompanyDetailEntity.SymbolConstant },
                        { id: PrHealthInsuranceCompanyDetailEntity.RemittanceInformation }
                    ]
                ]
            }
        ]
    };

    return {
        entitySet: EntitySetName.PrPayrollSettings,
        form
    };
};

getDefinitions.translationFiles = ["InsuranceCompanies", "Common"];